import { Table, Tag } from '@surecompanies/core_components'
import type { ColumnsType } from 'antd/es/table'
import React from 'react'
import type {
  BenefitRow,
  OverlapEnrollment,
} from '../api/enrollment-overlap.types'
import {
  EMPTY_VALUE,
  formatActive,
  formatDate,
  orDash,
  zipBenefits,
} from '../utils/run-display'

type KeyedBenefitRow = BenefitRow & { key: string }

const BENEFIT_COLUMNS: ColumnsType<KeyedBenefitRow> = [
  { title: 'Type', key: 'type', render: (_, row) => orDash(row.type) },
  { title: 'Plan', key: 'name', render: (_, row) => orDash(row.name) },
  { title: 'Carrier', key: 'carrier', render: (_, row) => orDash(row.carrier) },
  {
    title: 'Active',
    key: 'isActive',
    render: (_, row) => formatActive(row.isActive),
  },
  {
    title: 'Application status',
    key: 'applicationStatus',
    render: (_, row) => orDash(row.applicationStatus),
  },
  { title: 'Benefit ID', key: 'id', render: (_, row) => orDash(row.id) },
]

function BenefitsTable({ enrollment }: { enrollment: OverlapEnrollment }) {
  const rows = zipBenefits(enrollment.benefits).map((row, index) => ({
    ...row,
    key: `${enrollment.id}-${index}`,
  }))
  return (
    <Table<KeyedBenefitRow>
      size="small"
      pagination={false}
      dataSource={rows}
      columns={BENEFIT_COLUMNS}
      rowKey="key"
      locale={{ emptyText: 'No active benefits' }}
    />
  )
}

function cartStatusText(enrollment: OverlapEnrollment): React.ReactNode {
  if (!enrollment.cartStatus && enrollment.cartCommitted === null) {
    return EMPTY_VALUE
  }
  return (
    <>
      {orDash(enrollment.cartStatus)}
      {enrollment.cartCommitted ? (
        <Tag style={{ marginLeft: 8 }}>Committed</Tag>
      ) : null}
    </>
  )
}

function carriersAndPlans(enrollment: OverlapEnrollment): string {
  const rows = zipBenefits(enrollment.benefits)
  if (rows.length === 0) return EMPTY_VALUE
  const parts = rows
    .map((row) => [row.carrier, row.name].filter(Boolean).join(' – '))
    .filter((text) => text !== '')
  return parts.length > 0 ? parts.join(', ') : EMPTY_VALUE
}

const COLUMNS: ColumnsType<OverlapEnrollment> = [
  { title: 'Enrollment ID', dataIndex: 'id', key: 'id' },
  { title: 'Type', key: 'type', render: (_, row) => orDash(row.type) },
  { title: 'Start', key: 'start', render: (_, row) => formatDate(row.start) },
  { title: 'End', key: 'end', render: (_, row) => formatDate(row.end) },
  {
    title: 'Cart status',
    key: 'cartStatus',
    render: (_, row) => cartStatusText(row),
  },
  {
    title: 'Carriers / plans',
    key: 'carriers',
    render: (_, row) => carriersAndPlans(row),
  },
]

interface EnrollmentsTableProps {
  enrollments: OverlapEnrollment[]
}

export default function EnrollmentsTable({
  enrollments,
}: EnrollmentsTableProps) {
  return (
    <Table<OverlapEnrollment>
      dataSource={enrollments}
      columns={COLUMNS}
      rowKey={(enrollment) => enrollment.id}
      pagination={false}
      expandable={{
        expandedRowRender: (enrollment) => (
          <BenefitsTable enrollment={enrollment} />
        ),
        rowExpandable: (enrollment) => enrollment.benefits !== null,
      }}
      locale={{ emptyText: 'No enrollments' }}
    />
  )
}
